import React, { useState, useEffect } from "react";
import { ref, get } from "firebase/database";
import { auth, db } from "./firebase";
import { FiFileText, FiCheckCircle, FiCircle } from "react-icons/fi";
import { DashboardLoading, DashboardNotice, EmptyState, NoCourse } from "./DashboardStates";
import { useAuth } from "./AuthContext";

const PastPapers = () => {
  const [pastPapers, setPastPapers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { enrolledCourse } = useAuth();
  const [userCourse, setUserCourse] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError("User not authenticated. Please log in.");
          setLoading(false);
          return;
        }

        const email = currentUser.email;
        let foundUser = null;
        let userCourseId = null;

        // First check if user is admin/assistant in users collection
        const usersRef = ref(db, "users");
        const usersSnapshot = await get(usersRef);

        if (usersSnapshot.exists()) {
          const usersData = usersSnapshot.val();
          foundUser = Object.values(usersData).find(
            (u) => u.email === email
          );
          if (foundUser) {
            userCourseId = foundUser.enrolledCourse;
          }
        }

        // If not found in users collection, search in courses/students
        if (!foundUser) {
          const coursesRef = ref(db, "courses");
          const coursesSnapshot = await get(coursesRef);

          if (coursesSnapshot.exists()) {
            const courses = coursesSnapshot.val();

            // Search through each course's students
            for (const [courseId, courseData] of Object.entries(courses)) {
              if (courseData.students) {
                const student = Object.values(courseData.students).find(
                  (s) => s.email === email
                );
                if (student) {
                  foundUser = student;
                  userCourseId = courseId;
                  break;
                }
              }
            }
          }
        }

        if (!userCourseId) {
          setLoading(false);
          return;
        }

        setUserCourse(userCourseId);

        const papersRef = ref(db, `courses/${userCourseId}/pastpapers`);
        const snapshot = await get(papersRef);

        if (snapshot.exists()) {
          const papersData = snapshot.val();
          setPastPapers(papersData);
        } else {
          setPastPapers({});
        }
      } catch (error) {
        console.error("Error fetching past papers:", error);
        setError("Failed to fetch past papers. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <DashboardLoading />;
  if (error) return <DashboardNotice message={error} />;
  if (!userCourse) return <NoCourse description="Once you're enrolled, past papers will show up here." />;

  return (
    <div className="mx-auto px-4 py-6 w-full">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-sky-600 rounded-2xl p-6 md:p-8 shadow-xl mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Past Papers</h1>
            <p className="text-blue-100">Access all available past papers for your course</p>
          </div>
        </div>
      </div>


      {/* Past Papers List */}
      <div className="grid grid-cols-1 gap-4 md:gap-6">
        {Object.keys(pastPapers).length === 0 ? (
          <EmptyState
            icon={FiFileText}
            title="No past papers yet"
            description="Exam papers will appear here, grouped by year, once they're added to your course."
          />
        ) : (
          Object.keys(pastPapers)
            .sort((a, b) => b - a) // Show newest year first
            .map((year) => (
              <div
                key={year}
                className="dash-card bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden hover:border-blue-500/30 transition-all duration-300"
              >
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                      <FiFileText className="text-xl" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">{year}</h3>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {Object.keys(pastPapers[year]).map((session) => (
                      <div key={session} className="mb-6 last:mb-0">
                        <h5 className="text-md font-semibold text-blue-400 mb-3">{session} Session</h5>
                        {Object.keys(pastPapers[year][session]).map((folder) => (
                          <div key={folder} className="mb-4 last:mb-0">
                            <h6 className="text-sm font-medium text-gray-400 mb-2">{folder}</h6>
                            <div className="grid grid-cols-1 gap-3">
                              {Object.entries(pastPapers[year][session][folder]).map(([key, paper]) => (
                                <a
                                  key={key}
                                  href={paper.file}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center p-4 dash-inset bg-gray-700/30 rounded-lg hover:bg-gray-700/50 transition-colors"
                                >
                                  <div className="flex items-center space-x-3">
                                    {paper.completed ? (
                                      <FiCheckCircle className="text-green-500 text-xl" />
                                    ) : (
                                      <FiCircle className="text-gray-400 text-xl" />
                                    )}
                                    <div>
                                      <h4 className="text-white font-medium">{paper.name}</h4>
                                      {paper.description && (
                                        <p className="text-sm text-gray-400">{paper.description}</p>
                                      )}
                                    </div>
                                  </div>
                                </a>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
        )}
      </div>
    </div>
  );
};

export default PastPapers;